import { ticketsApi } from "#/api/ticketsApi";
import { showSuccessNotification } from "#/components/notifications";
import type { CreateTicketPayload, Ticket } from "#/types/tickets";
import { TicketStatus } from "#/types/tickets";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import KanbanColumn from "./KanbanColumn";
import { TicketPreviewCard } from "./TicketPreviewCard";

type TicketColumns = {
  [key in TicketStatus]: Ticket[];
};

export interface Props {
  loading: boolean;
  tickets: Ticket[];
}

export const KanbanBoard = ({ loading = true, tickets }: Props) => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<TicketColumns>(
    Object.values(TicketStatus).reduce((acc, status) => {
      acc[status] = [];
      return acc;
    }, {} as TicketColumns)
  );
  const [activeItem, setActiveItem] = useState<Ticket | null>(null);
  const [updatingTicketId, setUpdatingTicketId] = useState<number | null>(null);
  const [overColumnKey, setOverColumnKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const findColumn = (id: number) => {
    return Object.entries(columns).find(([_, items]) => { 
      console.log("🚀 ~ findColumn ~ _:", _)
      return items.some((i) => i.id === id)
    });
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (!over) {
      setOverColumnKey(null);
      return;
    }

    const overId = over.id?.toString();

    // Check if overId is a column itself
    if (Object.keys(columns).includes(overId)) {
      setOverColumnKey(overId);
      return;
    }

    // Otherwise, find which column the item is in
    const matchedColumn = Object.entries(columns).find(([_, items]) => {
      console.log("🚀 ~ handleDragOver ~ _:", _)
      return items.some((item) => item.id.toString() === overId)
    }
    );

    if (matchedColumn) {
      setOverColumnKey(matchedColumn[0]); // column key
    } else {
      setOverColumnKey(null);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const ticketId = active.id as number;
    const found = Object.values(columns)
      .flat()
      .find((i) => i.id === ticketId);
    if (found) setActiveItem(found);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setOverColumnKey(null);
    const { active, over } = event;
    setActiveItem(null);
    if (!over) return;

    const activeId = active.id as number;
    const overId = over.id as number;

    const sourceColumnEntry = findColumn(activeId);
    const destinationColumnEntry =
      findColumn(Number(overId)) ??
      (Object.keys(columns).includes(String(over.id))
        ? [over.id as TicketStatus, columns[over.id as TicketStatus]]
        : undefined);

    if (!sourceColumnEntry || !destinationColumnEntry) {
      const destinationColumnKey = over.id as TicketStatus;
      if (columns[destinationColumnKey]) {
        const sourceKey = Object.entries(columns).find(([_, items]) => { 
          console.log("🚀 ~ handleDragEnd ~ _:", _)
          return items.some((i) => i.id === activeId)
        }
        )?.[0] as TicketStatus;
        if (!sourceKey) return;

        const sourceItems = [...columns[sourceKey]];
        const [moved] = sourceItems.splice(
          sourceItems.findIndex((i) => i.id === activeId),
          1
        );

        const destItems = [...columns[destinationColumnKey]];
        destItems.push(moved);

        setColumns((prev) => ({
          ...prev,
          [sourceKey]: sourceItems,
          [destinationColumnKey]: destItems,
        }));
      }
      return;
    }

    const [sourceKey, sourceItems] = sourceColumnEntry as [
      TicketStatus,
      Ticket[],
    ];
    const [destKey, destItems] = destinationColumnEntry as [
      TicketStatus,
      Ticket[],
    ];

    const sourceIndex = sourceItems.findIndex((i) => i.id === activeId);
    const destIndex = destItems.findIndex((i) => i.id === overId);

    if (sourceKey === destKey) {
      const newItems = arrayMove(sourceItems, sourceIndex, destIndex);
      setColumns((prev) => ({ ...prev, [sourceKey]: newItems }));
    } else {
      const sourceCopy = [...sourceItems];
      const destCopy = [...destItems];
      const [moved] = sourceCopy.splice(sourceIndex, 1);
      if (destIndex === -1) {
        destCopy.push(moved);
      } else {
        destCopy.splice(destIndex, 0, moved);
      }
      setColumns((prev) => ({
        ...prev,
        [sourceKey]: sourceCopy,
        [destKey]: destCopy,
      }));
      updateTicket(moved.id, { status: destKey });
    }
  };

  const updateTicket = (id: number, payload: Partial<CreateTicketPayload>) => {
    setUpdatingTicketId(id);
    ticketsApi
      .update(id, payload)
      .then((res) => {
        showSuccessNotification({
          message: t("tickets.updatedSuccess"),
        });

        const updatedTicket = res.data;
        setColumns((prev) => {
          const updatedColumns = { ...prev };
          for (const key of Object.keys(updatedColumns) as TicketStatus[]) {
            updatedColumns[key] = updatedColumns[key].filter(
              (t) => t.id !== updatedTicket.id
            );
          }
          updatedColumns[updatedTicket.status].push(
            updatedTicket
          );

          return updatedColumns;
        });
        setUpdatingTicketId(null);
      })
      .catch((err) => {
        console.error("❌ Failed to update ticket status", err);
        setUpdatingTicketId(null);
      });
  };

  useEffect(() => {
    const newColumns: TicketColumns = {
      [TicketStatus.Requested]: [],
      [TicketStatus.InProgress]: [],
      [TicketStatus.Done]: [],
      [TicketStatus.ProblemTickets]: [],
    };

    for (const ticket of tickets) {
      if (ticket.status === TicketStatus.Requested)
        newColumns[TicketStatus.Requested].push(ticket);
      else if (ticket.status === TicketStatus.InProgress)
        newColumns[TicketStatus.InProgress].push(ticket);
      else if (ticket.status === TicketStatus.Done)
        newColumns[TicketStatus.Done].push(ticket);
      else newColumns[TicketStatus.ProblemTickets].push(ticket);
    }

    setColumns(newColumns);
  }, [tickets]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="w-full h-full flex gap-4 overflow-x-auto lg:grid lg:grid-cols-[repeat(4,minmax(275px,1fr))]">
        {Object.entries(columns).map(([key, items]) => (
          <KanbanColumn
            key={key}
            keyId={key as TicketStatus}
            items={items}
            isOver={overColumnKey === key}
            loading={loading}
            updatingTicketId={updatingTicketId}
            updateTicket={updateTicket}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? <TicketPreviewCard ticket={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
